import {
  RETAIL_POS_CONFIGURATION_VERSION,
} from "./retailPOSConfiguration";

import {
  RETAIL_STUDIO_DOMAIN_ID,
  isRetailStudioDocument,
} from "./retailStudioDocument";

import {
  retailStudioRegistration,
} from "./retailStudioRegistration";


export class RetailStudioPublicationBindingError
  extends Error {
  constructor(
    message,
    {
      code =
        "RETAIL_STUDIO_PUBLICATION_BINDING_ERROR",
      details = {},
      cause = null,
    } = {}
  ) {
    super(message);

    this.name =
      "RetailStudioPublicationBindingError";

    this.code = code;

    this.details = Object.freeze({
      ...details,
    });

    if (cause !== null) {
      this.cause = cause;
    }
  }
}


/**
 * Retail Studio Publication Binding.
 *
 * Resolves the authoritative Retail StudioDocument
 * for one explicit company + activity scope and
 * materializes the StudioReference required by
 * ActivePublicationResolver.
 *
 * This binding is intentionally:
 *
 * - stateless;
 * - frontend-only;
 * - repository-free;
 * - publication engine-free;
 * - POS domain resolver-free;
 * - Activity resolver-free.
 */
export function createRetailStudioPublicationBinding({
  listDocuments,
  studioRegistration =
    retailStudioRegistration,
  domainId =
    RETAIL_STUDIO_DOMAIN_ID,
  runtimeVersion =
    RETAIL_POS_CONFIGURATION_VERSION,
} = {}) {
  assertListDocuments(listDocuments);

  const studioId =
    normalizeRequiredString(
      studioRegistration?.id,
      "studioRegistration.id",
      "INVALID_RETAIL_STUDIO_REGISTRATION"
    );

  const normalizedDomainId =
    normalizeRequiredString(
      domainId,
      "domainId",
      "INVALID_RETAIL_STUDIO_DOMAIN"
    );

  if (
    normalizedDomainId
    !== RETAIL_STUDIO_DOMAIN_ID
  ) {
    throw new RetailStudioPublicationBindingError(
      `Retail Studio publication binding requires domainId "${RETAIL_STUDIO_DOMAIN_ID}".`,
      {
        code:
          "INVALID_RETAIL_STUDIO_DOMAIN",
        details: {
          expected:
            RETAIL_STUDIO_DOMAIN_ID,
          received:
            normalizedDomainId,
        },
      }
    );
  }

  const normalizedRuntimeVersion =
    normalizeRuntimeVersion(
      runtimeVersion
    );

  async function resolveStudioReference(
    scope = {}
  ) {
    const companyUuid =
      normalizeRequiredString(
        scope?.companyUuid,
        "companyUuid",
        "MISSING_COMPANY_SCOPE"
      );

    const activityUuid =
      normalizeRequiredString(
        scope?.activityUuid,
        "activityUuid",
        "MISSING_ACTIVITY_SCOPE"
      );

    if (
      scope?.domainId !== undefined
      && scope?.domainId !== null
    ) {
      const requestedDomainId =
        normalizeRequiredString(
          scope.domainId,
          "domainId",
          "INVALID_RETAIL_STUDIO_DOMAIN"
        );

      if (
        requestedDomainId
        !== normalizedDomainId
      ) {
        throw new RetailStudioPublicationBindingError(
          `Retail Studio publication binding cannot resolve domain "${requestedDomainId}".`,
          {
            code:
              "RETAIL_STUDIO_DOMAIN_MISMATCH",
            details: {
              expected:
                normalizedDomainId,
              received:
                requestedDomainId,
            },
          }
        );
      }
    }

    let response;

    try {
      response =
        await listDocuments({
          companyUuid,
          activityUuid,
        });
    } catch (error) {
      throw new RetailStudioPublicationBindingError(
        "Unable to list Studio documents for the Retail publication scope.",
        {
          code:
            "STUDIO_DOCUMENT_LIST_FAILED",
          details: {
            companyUuid,
            activityUuid,
          },
          cause: error,
        }
      );
    }

    const documents =
      extractDocuments(response);

    const retailDocuments =
      documents
        .map(extractStudioDocument)
        .filter(
          (document) =>
            document !== null
            && isRetailStudioDocument(
              document
            )
        );

    if (retailDocuments.length === 0) {
      throw new RetailStudioPublicationBindingError(
        "No authoritative Retail StudioDocument exists for the requested activity.",
        {
          code:
            "RETAIL_STUDIO_DOCUMENT_NOT_FOUND",
          details: {
            companyUuid,
            activityUuid,
          },
        }
      );
    }

    if (retailDocuments.length > 1) {
      throw new RetailStudioPublicationBindingError(
        "Multiple authoritative Retail StudioDocuments exist for the requested activity.",
        {
          code:
            "AMBIGUOUS_RETAIL_STUDIO_DOCUMENT",
          details: {
            companyUuid,
            activityUuid,
            documentIds:
              Object.freeze(
                retailDocuments.map(
                  (document) =>
                    document?.uuid
                    ?? null
                )
              ),
          },
        }
      );
    }

    const document =
      retailDocuments[0];

    const documentId =
      normalizeRequiredString(
        document?.uuid,
        "document.uuid",
        "INVALID_RETAIL_STUDIO_DOCUMENT_ID"
      );

    const documentDomainId =
      normalizeRequiredString(
        document?.metadata?.domainId,
        "document.metadata.domainId",
        "INVALID_RETAIL_STUDIO_DOCUMENT_DOMAIN"
      );

    if (
      documentDomainId
      !== normalizedDomainId
    ) {
      throw new RetailStudioPublicationBindingError(
        "Retail StudioDocument domain does not match the Retail publication domain.",
        {
          code:
            "RETAIL_STUDIO_DOCUMENT_DOMAIN_MISMATCH",
          details: {
            expected:
              normalizedDomainId,
            received:
              documentDomainId,
            documentId,
          },
        }
      );
    }

    return Object.freeze({
      studioId,
      documentId,
      domainId:
        normalizedDomainId,
      runtimeVersion:
        normalizedRuntimeVersion,
    });
  }

  return Object.freeze({
    resolveStudioReference,
  });
}


function extractDocuments(response) {
  const documents =
    response?.data?.documents
    ?? response?.documents
    ?? response;

  if (!Array.isArray(documents)) {
    throw new RetailStudioPublicationBindingError(
      "Studio document listing must return an array of documents.",
      {
        code:
          "INVALID_STUDIO_DOCUMENT_LIST",
        details: {
          receivedType:
            documents === null
              ? "null"
              : typeof documents,
        },
      }
    );
  }

  return documents;
}


/**
 * StudioPersistence may expose serialized
 * StudioDocument envelopes:
 *
 * {
 *   schemaVersion,
 *   document
 * }
 *
 * while injected tests may provide the raw
 * StudioDocument directly.
 */
function extractStudioDocument(value) {
  if (
    value !== null
    && typeof value === "object"
    && !Array.isArray(value)
    && value.document !== null
    && typeof value.document === "object"
    && !Array.isArray(value.document)
  ) {
    return value.document;
  }

  return value;
}


function assertListDocuments(
  listDocuments
) {
  if (
    typeof listDocuments
    !== "function"
  ) {
    throw new RetailStudioPublicationBindingError(
      "listDocuments must be a function.",
      {
        code:
          "INVALID_STUDIO_DOCUMENT_LIST_PROVIDER",
      }
    );
  }
}


function normalizeRequiredString(
  value,
  field,
  code
) {
  if (
    typeof value !== "string"
    || !value.trim()
  ) {
    throw new RetailStudioPublicationBindingError(
      `${field} must be a non-empty string.`,
      {
        code,
        details: {
          field,
        },
      }
    );
  }

  return value.trim();
}


function normalizeRuntimeVersion(
  value
) {
  if (
    !Number.isInteger(value)
    || value <= 0
  ) {
    throw new RetailStudioPublicationBindingError(
      "runtimeVersion must be a positive integer.",
      {
        code:
          "INVALID_RETAIL_RUNTIME_VERSION",
        details: {
          runtimeVersion:
            value ?? null,
        },
      }
    );
  }

  return value;
}


export default
  createRetailStudioPublicationBinding;
